/**
 * Synonyms, keyed by nav item id.
 *
 * This map is the product. The panel itself is a library; whether anyone finds
 * anything comes down to these words, because people type what they call the
 * thing, not what we labelled it. Someone looking for redlines searches
 * "redline", never "Contracts".
 *
 * Deliberately NOT seeded from the Slack "how do I" corpus: a 20-message sample
 * of those was entirely Nowadays staff asking about the admin dashboard, so it
 * would index our vocabulary rather than a client's. Seeded from the product's
 * own surfaces instead, and corrected over time by the zero-result log.
 */
export const KEYWORDS_BY_ID: Record<string, string[]> = {
  // Event
  home: ["home", "overview", "summary", "getting started", "dashboard"],
  details: [
    "event details",
    "dates",
    "headcount",
    "budget",
    "location",
    "change details",
    // People reach for the verb before the noun: "edit" returned nothing at all
    // until these landed, which is the first thing Anna tried.
    "edit",
    "rename",
    "update",
  ],
  users: [
    "invite",
    "add someone",
    "teammate",
    "colleague",
    "access",
    "permissions",
    "seat",
    "who can see",
    "event users",
    "users",
    "people",
  ],
  notes: ["scratchpad", "memo", "comments"],
  // "chat" is Chat's, and "ask" with it: this is a thread with a hotel, not the
  // assistant, and two rows answering one word is a query with no answer.
  messages: ["conversation", "message a hotel", "reply", "inbox", "thread", "contact hotel", "question", "follow up"],
  chat: ["ai", "assistant", "ask", "help", "copilot", "recommend"],

  // Source
  search: ["venue", "hotel", "find a venue", "sourcing", "properties", "options", "shortlist"],
  restaurant: ["dinner", "group dining", "private dining", "restaurant", "food"],
  event_space: ["meeting room", "breakout", "boardroom", "capacity", "sqft", "av", "offsite space"],
  proposals: [
    "quote",
    "rate",
    "nightly",
    "per night",
    "price",
    "fnb",
    "f&b",
    "food and beverage",
    "minimum",
    "resort fee",
    "taxes",
    "bids",
  ],
  dinners: ["restaurant quotes", "dinner quotes", "inquiries", "replies", "book a restaurant", "hold"],
  compare: ["side by side", "versus", "vs", "which is cheaper", "shortlist", "top 2", "apples to apples", "comparison"],
  contracts: ["redline", "markup", "legal", "terms", "cancellation", "attrition", "force majeure", "sign", "agreement"],
  payments: ["invoice", "deposit", "pay", "billing", "receipt", "refund"],

  // Manage / registration
  registration: ["rsvp", "sign up", "signup", "attendees", "guest list", "headcount", "register"],
  "landing-pages": ["website", "event page", "microsite", "share link", "landing page"],
  forms: ["rsvp form", "questionnaire", "survey", "collect", "sign up form"],
  activities: ["sessions", "signups", "waitlist", "excursions", "optional"],
  schedule: ["agenda", "timetable", "run of show", "itinerary"],
  notifications: ["email attendees", "send invite", "reminder", "comms", "communications", "blast"],
  overview: ["registration overview", "summary"],

  // Organization
  events: ["my events", "all events", "list", "switch event", "other events"],
  settings: ["preferences", "configure", "org settings"],
  approvals: ["approve", "sign off", "request approval", "authorisation", "authorization"],
  reporting: ["spend", "budget", "savings", "how much have we spent", "analytics", "report"],
  // Personal settings, shown under org Settings. Kept apart from Registration's "notifications" above.
  "personal-notifications": ["notifications", "slack", "dm", "email preferences", "planner-bot"],
};

/**
 * Synonyms for the org-settings and reporting leaves, keyed by the last path
 * segment. Those live as `children` under one nav item, so they cannot be keyed
 * by a unique top-level id.
 */
export const KEYWORDS_BY_LEAF: Record<string, string[]> = {
  profile: ["org profile", "company", "logo", "name"],
  "office-location": ["offices", "locations", "headquarters", "hq"],
  "preferred-venues": ["preferred", "approved venues", "allowlist", "favourites", "favorites"],
  "blacklisted-venues": ["blacklist", "banned", "do not use", "blocked venues"],
  policies: ["travel policy", "rules", "limits", "compliance"],
  "required-questions": ["rfp questions", "venue questions", "questionnaire"],
  integrations: ["connect", "sso", "api", "sync", "navan", "spotnana"],
  groups: ["teams", "departments", "user groups"],
  overview: ["reporting overview", "summary"],
  spend: ["how much", "cost", "total spend", "budget"],
  savings: ["saved", "negotiated", "discount"],
  "duty-of-care": ["safety", "where are my people", "risk", "traveller tracking"],
  sustainability: ["carbon", "emissions", "esg", "footprint"],
  "user-satisfaction": ["nps", "feedback", "survey results"],
  "tool-usage": ["adoption", "usage", "logins"],
};
